// ============================================================================
// HOST A TOURNAMENT (creators and admins)
// ----------------------------------------------------------------------------
// Two steps, no typing of blind levels: pick a buy-in preset, a speed, a
// start time and who can enter; then (only when allowed) voice / video.
// The server builds the structure from the same presets the daily catalog
// uses. Creators get CREATOR_FREE_TOURNAMENTS_PER_MONTH free per month; the
// next one is created as "awaiting approval" and the creator is told so
// before they publish.
// ============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  VStack, HStack, Box, Text, Input, FormControl, FormLabel, Button, Badge, useToast, Alert, AlertIcon, AlertDescription,
} from '@chakra-ui/react';
import { Mic, Video, VideoOff, Trophy, Users, Lock, Globe, KeyRound } from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';
import gameApiService, { HostPermissions, HostQuota } from '../../services/gameApiService';

type Media = 'off' | 'voice' | 'video';
type Scope = 'all' | 'final';
type Entry = 'code' | 'group' | 'open';

interface HostTournamentModalProps {
  isOpen: boolean;
  onClose: () => void;
  permissions: HostPermissions;
  quota: HostQuota;
  creatorGroupName?: string | null;
  onCreated?: (r: { tournamentId: string; status: string; inviteCode: string | null }) => void;
}

const STRUCTURE_LABEL: Record<string, { name: string; blurb: string }> = {
  regular: { name: 'Regular', blurb: '10-minute levels. About 2 to 3 hours.' },
  turbo: { name: 'Turbo', blurb: '5-minute levels. About an hour.' },
  hyper: { name: 'Hyper', blurb: '2-minute levels. 30 to 40 minutes.' },
  deep: { name: 'Deep', blurb: '15-minute levels, deeper stacks. A long night.' },
};

const Seg: React.FC<{ value: string; options: Array<{ v: string; label: React.ReactNode; disabled?: boolean }>; onChange: (v: string) => void }> = ({ value, options, onChange }) => (
  <HStack spacing={1} bg="#06141F" p={1} borderRadius="md" flexWrap="wrap">
    {options.map(o => (
      <Button
        key={o.v} size="sm" flex={1} minW="64px" variant="ghost" isDisabled={o.disabled}
        bg={value === o.v ? POKER_COLORS.cyan : 'transparent'} color={value === o.v ? '#06141F' : 'gray.300'}
        _hover={{ bg: value === o.v ? POKER_COLORS.cyan : 'whiteAlpha.100' }}
        onClick={() => onChange(o.v)}
      >{o.label}</Button>
    ))}
  </HStack>
);

const toLocalInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const HostTournamentModal: React.FC<HostTournamentModalProps> = ({ isOpen, onClose, permissions, quota, creatorGroupName, onCreated }) => {
  const toast = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState<'playcoins' | 'pkcash'>('playcoins');
  const [buyIn, setBuyIn] = useState<number>(0);
  const [structure, setStructure] = useState('regular');
  const [startAt, setStartAt] = useState(() => toLocalInput(new Date(Date.now() + 2 * 60 * 60 * 1000)));
  const [maxPlayers, setMaxPlayers] = useState(90);
  const [entry, setEntry] = useState<Entry>('code');
  const [media, setMedia] = useState<Media>('off');
  const [scope, setScope] = useState<Scope>('final');
  const [mediaComped, setMediaComped] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const mediaAllowed = permissions.mediaAllowed || 'none';
  const canVoice = mediaAllowed === 'voice' || mediaAllowed === 'video';
  const canVideo = mediaAllowed === 'video';
  const buyIns = permissions.buyIns?.[currency] || (currency === 'pkcash' ? [1, 2, 5, 10, 25, 50] : [0, 500, 1000, 2500, 5000, 10000]);

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setBuyIn(buyIns[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  useEffect(() => { if (!buyIns.includes(buyIn)) setBuyIn(buyIns[0]); }, [currency, buyIns, buyIn]);

  const willNeedApproval = !permissions.isAdmin && quota.nextNeedsApproval;
  const startDate = useMemo(() => new Date(startAt), [startAt]);
  const startOk = !isNaN(startDate.getTime()) && startDate.getTime() > Date.now() + (permissions.isAdmin ? 60_000 : 30 * 60_000);

  const estimateLine = useMemo(() => {
    if (media === 'off') return null;
    const hours = structure === 'hyper' ? 0.6 : structure === 'turbo' ? 1.2 : structure === 'deep' ? 4 : 2.5;
    const mins = Math.round(maxPlayers * hours * 60);
    return `${mins.toLocaleString()} table minutes if it fills. ${mediaComped || permissions.mediaComped ? 'Covered for every player.' : 'Players use their free weekly minutes or a pass, as on cash tables.'}`;
  }, [media, structure, maxPlayers, mediaComped, permissions.mediaComped]);

  const submit = async () => {
    if (name.trim().length < 3) { toast({ title: 'Give it a name (3+ characters)', status: 'warning', duration: 3000 }); setStep(1); return; }
    if (!startOk) { toast({ title: permissions.isAdmin ? 'Start must be in the future' : 'Start at least 30 minutes from now', status: 'warning', duration: 3000 }); setStep(1); return; }
    setSubmitting(true);
    const r = await gameApiService.hostCreateTournament({
      name: name.trim(), currency, buyIn, structure, startTime: startDate.toISOString(), maxPlayers, entry,
      media, mediaScope: scope, mediaComped: permissions.isAdmin ? mediaComped : undefined,
    });
    setSubmitting(false);
    if (!r.success) { toast({ title: 'Could not publish', description: r.error, status: 'error', duration: 6000 }); return; }
    toast({
      title: r.status === 'pending_approval' ? 'Sent for approval' : 'Tournament is live',
      description: r.status === 'pending_approval'
        ? 'You used your free tournaments this month. We will approve this one shortly and let you know.'
        : r.inviteCode ? `Share code ${r.inviteCode} with your crew.` : 'It is in the Tournaments lobby now.',
      status: r.status === 'pending_approval' ? 'info' : 'success', duration: 7000, isClosable: true,
    });
    onCreated?.({ tournamentId: r.tournamentId!, status: r.status!, inviteCode: r.inviteCode ?? null });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent bg="#0D1B2A" border="1px solid" borderColor={POKER_COLORS.cyan} color="white">
        <ModalHeader>
          <HStack><Trophy size={20} color="#FFD700" /><Text>Host a tournament</Text><Badge ml={2} colorScheme="cyan">step {step} of 2</Badge></HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {step === 1 ? (
            <VStack spacing={4} align="stretch">
              {willNeedApproval && (
                <Alert status="info" bg="rgba(0,217,217,0.08)" border="1px solid rgba(0,217,217,0.35)" borderRadius="md">
                  <AlertIcon color={POKER_COLORS.cyan} />
                  <AlertDescription fontSize="sm">
                    You have used your {quota.freePerMonth} free tournaments this month. This one will be sent to PSS for a quick approval before it appears in the lobby.
                  </AlertDescription>
                </Alert>
              )}
              <FormControl>
                <FormLabel fontSize="sm" color="gray.300">Name</FormLabel>
                <Input id="host-t-name" value={name} onChange={e => setName(e.target.value)} placeholder="Friday Night Invitational" maxLength={40} bg="#06141F" borderColor="gray.600" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.300">Buy-in</FormLabel>
                <Seg value={currency} onChange={v => setCurrency(v as any)} options={[{ v: 'playcoins', label: 'PlayCoins' }, { v: 'pkcash', label: 'PSS Coins' }]} />
                <Box mt={2}>
                  <Seg value={String(buyIn)} onChange={v => setBuyIn(Number(v))} options={buyIns.map(b => ({ v: String(b), label: currency === 'pkcash' ? `$${b}` : b === 0 ? 'Free' : b.toLocaleString() }))} />
                </Box>
                <Text fontSize="xs" color="gray.500" mt={1}>{currency === 'pkcash' ? 'Real prizes. 10% house rake, paid out top 3.' : 'PlayCoins only, no rake. Great for a first one.'}</Text>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.300">Speed</FormLabel>
                <Seg value={structure} onChange={setStructure} options={(permissions.structures || Object.keys(STRUCTURE_LABEL)).map(s => ({ v: s, label: STRUCTURE_LABEL[s]?.name || s }))} />
                <Text fontSize="xs" color="gray.500" mt={1}>{STRUCTURE_LABEL[structure]?.blurb}</Text>
              </FormControl>
              <HStack align="start" spacing={4} flexWrap="wrap">
                <FormControl flex={1} minW="200px">
                  <FormLabel fontSize="sm" color="gray.300">Starts</FormLabel>
                  <Input id="host-t-start" type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} bg="#06141F" borderColor={startOk ? 'gray.600' : 'red.400'} sx={{ colorScheme: 'dark' }} />
                  <Text fontSize="xs" color={startOk ? 'gray.500' : 'red.300'} mt={1}>{startOk ? 'Your local time. Late registration stays open for the first levels.' : 'Pick a time at least 30 minutes out.'}</Text>
                </FormControl>
                <FormControl w="140px">
                  <FormLabel fontSize="sm" color="gray.300">Max players</FormLabel>
                  <Input id="host-t-max" type="number" min={6} max={500} value={maxPlayers} onChange={e => setMaxPlayers(Math.max(6, Math.min(500, Number(e.target.value) || 6)))} bg="#06141F" borderColor="gray.600" />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.300">Who can enter</FormLabel>
                <Seg value={entry} onChange={v => setEntry(v as Entry)} options={[
                  { v: 'code', label: <HStack spacing={1}><KeyRound size={13} /><Text>Code</Text></HStack> },
                  { v: 'group', label: <HStack spacing={1}><Users size={13} /><Text>My crew</Text></HStack>, disabled: !creatorGroupName },
                  { v: 'open', label: <HStack spacing={1}><Globe size={13} /><Text>Anyone</Text></HStack> },
                ]} />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {entry === 'code' ? 'We generate a code. Drop it on stream or in your group; players enter it to register.'
                    : entry === 'group' ? `Only members of ${creatorGroupName || 'your group'} can register.`
                    : 'Listed for everyone in the lobby.'}
                </Text>
              </FormControl>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" color="gray.300">Voice and video at the tables</FormLabel>
                <Seg value={media} onChange={v => setMedia(v as Media)} options={[
                  { v: 'off', label: <HStack spacing={1}><VideoOff size={13} /><Text>Off</Text></HStack> },
                  { v: 'voice', label: <HStack spacing={1}><Mic size={13} /><Text>Voice</Text></HStack>, disabled: !canVoice },
                  { v: 'video', label: <HStack spacing={1}><Video size={13} /><Text>Voice + video</Text></HStack>, disabled: !canVideo },
                ]} />
                {!canVoice && <Text fontSize="xs" color="gray.500" mt={1}>Voice and video are switched on per creator by PSS. Ask your contact if you want them.</Text>}
                {canVoice && !canVideo && <Text fontSize="xs" color="gray.500" mt={1}>Voice is enabled for you. Video can be switched on by PSS.</Text>}
              </FormControl>
              {media === 'video' && (
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.300">Where video is on</FormLabel>
                  <Seg value={scope} onChange={v => setScope(v as Scope)} options={[{ v: 'final', label: 'Final table only' }, { v: 'all', label: 'Every table' }]} />
                  <Text fontSize="xs" color="gray.500" mt={1}>{scope === 'final' ? 'Voice everywhere, cameras come on when the final table forms. The showcase moment.' : 'Cameras on at every table from the first hand.'}</Text>
                </FormControl>
              )}
              {media !== 'off' && permissions.isAdmin && (
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.300">Cover it for everyone</FormLabel>
                  <Seg value={mediaComped ? 'yes' : 'no'} onChange={v => setMediaComped(v === 'yes')} options={[{ v: 'no', label: 'Players use their minutes' }, { v: 'yes', label: 'PSS covers all tables' }]} />
                </FormControl>
              )}
              {media !== 'off' && (
                <Box p={3} bg="#06141F" borderRadius="md" border="1px solid" borderColor="whiteAlpha.200">
                  <Text fontSize="xs" color="gray.400">Estimate</Text>
                  <Text fontSize="sm" color="white">{estimateLine}</Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>When a player is moved between tables, their mic comes with them. Nobody has to rejoin.</Text>
                </Box>
              )}
              <Box p={3} bg="rgba(255,215,0,0.06)" borderRadius="md" border="1px solid rgba(255,215,0,0.3)">
                <Text fontSize="sm" color="white" fontWeight="bold">{name || 'Untitled'} · {currency === 'pkcash' ? `$${buyIn}` : buyIn === 0 ? 'Free' : `${buyIn.toLocaleString()} PC`} · {STRUCTURE_LABEL[structure]?.name}</Text>
                <Text fontSize="xs" color="gray.400">{startOk ? startDate.toLocaleString() : 'start time needed'} · up to {maxPlayers} players · {entry === 'code' ? 'code entry' : entry === 'group' ? 'crew only' : 'open'} · {media === 'off' ? 'no voice' : media === 'voice' ? 'voice' : `video (${scope === 'final' ? 'final table' : 'all tables'})`}</Text>
              </Box>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          {step === 2 && <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {step === 1 ? (
            <Button colorScheme="cyan" onClick={() => setStep(2)} isDisabled={name.trim().length < 3 || !startOk}>Next{canVoice ? ': voice & video' : ''}</Button>
          ) : (
            <Button bg="#FFD700" color="#06141F" _hover={{ bg: '#e6c200' }} onClick={submit} isLoading={submitting} leftIcon={<Lock size={14} />}>
              {willNeedApproval ? 'Send for approval' : 'Publish'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default HostTournamentModal;
